import { batch, createContext, createEffect, createMemo, on, onMount, useContext, type ParentComponent } from "solid-js";
import { createStore, reconcile } from "solid-js/store";
import type { DateEvent } from "../../../../backend/shared";
import { getDateEvents, getPorts } from "../../shared/api/endpoints";
import type { ScheduleVoyage } from "../../shared/api/types";
import { useUpstream } from "../upstream-auth/store";
import { loadPortColors, nextColor, savePortColors } from "./colors";
import { buildPortCountries } from "./date-events";
import { DEFAULT_OPR_BUCKET, type OprBucketId } from "./opr-buckets";
import { applySelection } from "./selection";
import { laneAnchors, pruneOrphanedTemps, type TemporaryLane } from "./temporary-ports";
import { computeInitialViewport } from "./today-window";
import { mergeUpsertVoyages } from "./voyage-upsert";

// Single port group: { name: "ANTWERP", ports: ["ANTWERP - EUROPORT", ...] }
export interface PortGroup {
  name: string;
  ports: string[];
}

export interface CalendarState {
  portGroups: PortGroup[];
  portRefs: Record<string, string>;        // portName -> externalRef
  portToGroup: Record<string, string>;     // portName -> group name (for tri-state)
  selectedPorts: string[];                 // ordered for stable URL emit
  // Transient lanes the user revealed by clicking a hidden-stop target
  // icon on a vessel's journey line, keyed by port name. Rendered +
  // queried like selectedPorts (interleaved by anchor in queryPorts)
  // but never serialised to the URL or persisted via /api/current-view
  // — they live only for the session until the user pins them. One
  // record instead of parallel per-field maps so add/remove/prune are
  // atomic by construction (see TemporaryLane).
  temporaries: Record<string, TemporaryLane>;
  expandedGroups: Record<string, boolean>; // group name -> open?
  portSearch: string;
  portColors: Record<string, string>;      // portName -> #hex
  year: number;                             // 1..N
  month: number;                            // 1..12
  queryPorts: string[];                    // snapshot at runQuery time
  loadedMonths: Record<string, true>;       // "yyyy-mm" -> true
  voyages: Record<string, ScheduleVoyage>; // voyageItemId -> voyage
  // All date events from `/api/date-events` — both user-created rows
  // (source='user') and the backend-synced public holidays
  // (source='holiday'). Loaded once on mount; re-fetched on writes and
  // when a new (country, year) needs syncing.
  dateEvents: DateEvent[];
  viewport: { start: Date; end: Date };
  status: { text: string; tone: "info" | "loading" | "error" };
  isLoadingTimeline: boolean;
  isLoadingPorts: boolean;
  // Runtime swatch picks for selectedPorts / temp lanes that the
  // user HASN'T explicitly coloured via Settings. NOT persisted —
  // re-seeded by `ensureColors` at boot / on selection changes. Kept
  // separate from `portColors` so the itinerary's per-stop
  // border-left can read user-set colours directly without
  // accidentally picking up an auto-assigned swatch (e.g. Antwerp
  // Pilot Station getting emerald-500 from the palette pool).
  // Calendar bars + chips + lane labels read the MERGED view via
  // `effectivePortColors()` so they stay visually distinguishable.
  autoPortColors: Record<string, string>;
  // Active "investigate this vessel" filter (single vesselCode, or
  // null = no filter). Non-matching bars are dimmed (not hidden) so
  // journey-line context stays readable. A combined-bar cluster
  // counts as matching when ANY of its voyages belongs to the chosen
  // vessel. Bi-directionally linked to the calendar selection via
  // an effect in Calendar.tsx: clicking a stop sets the filter to
  // that vessel; picking a vessel in the FAB sets the selection to
  // a representative voyage of that vessel so journey connections
  // render. Driven by the floating FAB + URL key `c.vessel`.
  vesselFilter: string | null;
  // Active operator-type bucket. Drives the upstream `oprTypeFilter`
  // on /api/ports/schedule. Default `"all"` surfaces every voyage WW
  // has any commercial interest in; narrowing buckets refetch with a
  // shorter code list. See `./opr-buckets.ts` for the mapping.
  oprBucket: OprBucketId;
}

export function ymKey(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, "0")}`;
}

function initialViewport(): { start: Date; end: Date } {
  // Default "Today" window. Span scales with viewport width via
  // initialSpanDays (phone -> 1 week, desktop -> ~1 month, ultrawide
  // -> ~8 weeks); left anchor reuses the Today-button offset formula
  // so today sits ~10% from the left edge regardless of chosen span.
  const vw = typeof window === "undefined" ? 1280 : window.innerWidth;
  return computeInitialViewport(new Date(), vw);
}

function initialState(): CalendarState {
  const now = new Date();
  return {
    portGroups: [],
    portRefs: {},
    portToGroup: {},
    selectedPorts: [],
    temporaries: {},
    expandedGroups: {},
    portSearch: "",
    portColors: loadPortColors(),
    autoPortColors: {},
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    queryPorts: [],
    loadedMonths: {},
    voyages: {},
    dateEvents: [],
    viewport: initialViewport(),
    status: { text: "", tone: "info" },
    isLoadingTimeline: false,
    isLoadingPorts: false,
    vesselFilter: null,
    oprBucket: DEFAULT_OPR_BUCKET,
  };
}

export interface CalendarContextValue {
  state: CalendarState;
  // Port catalogue
  setPortCatalogue: (
    portGroups: PortGroup[],
    portRefs: Record<string, string>,
    portToGroup: Record<string, string>,
  ) => void;
  // Selection
  setPortsSelected: (ports: string[], selected: boolean) => void;
  setSelectedPorts: (ports: string[]) => void;
  // Temporary lanes (revealed from hidden-stop icon clicks)
  addTemporaryPort: (port: string, afterPort: string, sourceVesselCode: string, sourceVoyageNo: string, orderHint: number, externalRef?: string) => void;
  removeTemporaryPort: (port: string) => void;
  pinTemporaryPort: (port: string) => void;
  // UI
  toggleGroupExpanded: (groupName: string) => void;
  expandGroup: (groupName: string, open: boolean) => void;
  setPortSearch: (s: string) => void;
  // Colours
  setPortColor: (portName: string, hex: string) => void;
  clearPortColor: (portName: string) => void;
  ensureColors: () => void;
  resetPortColors: () => void;
  // Date controls
  setYearMonth: (year: number, month: number) => void;
  // Query / data
  setQueryPorts: (ports: string[]) => void;
  setViewport: (start: Date, end: Date) => void;
  markMonthLoaded: (year: number, month: number) => void;
  upsertVoyages: (voyages: ScheduleVoyage[]) => void;
  setDateEvents: (events: DateEvent[]) => void;
  // Status / loading
  setStatus: (text: string, tone: CalendarState["status"]["tone"]) => void;
  setTimelineLoading: (active: boolean) => void;
  setPortsLoading: (active: boolean) => void;
  // Vessel filter — single vessel or null. `toggleVesselFilter(code)`
  // sets it to `code`, or clears it if `code` already matches the
  // active filter (single-select toggle semantics).
  setVesselFilter: (code: string | null) => void;
  toggleVesselFilter: (code: string) => void;
  // Operator-type bucket. Switching the bucket clears loadedMonths +
  // voyages so the next viewport scroll refetches with the new filter.
  setOprBucket: (id: OprBucketId) => void;
  // Merged view of user-set + auto-assigned colours; user-set wins
  // on conflict. This is what every "needs a colour to render" call
  // site should read (calendar bars, lane chips, journey lines,
  // tooltip stripe, modal accent). The itinerary's per-stop
  // border-left deliberately reads `state.portColors` directly so it
  // only honours user-explicit picks.
  effectivePortColors: () => Record<string, string>;
}

const CalendarContext = createContext<CalendarContextValue>();

export const CalendarProvider: ParentComponent = (props) => {
  const [state, setState] = createStore<CalendarState>(initialState());

  // Memoised merge of user-set palette + auto-assigned palette. Every
  // call site (lane labels, journey lines, stevedore stripes, voyage
  // tooltip) previously rebuilt the spread on every read; memoising
  // returns the same Record reference until one of the inputs changes,
  // so dep-equality checks downstream skip recomputation. Solid tracks
  // both source proxies, so the memo invalidates correctly on a colour
  // pick or auto-assignment.
  const mergedPortColors = createMemo<Record<string, string>>(
    () => ({ ...state.autoPortColors, ...state.portColors }),
  );

  function setPortCatalogue(
    portGroups: PortGroup[],
    portRefs: Record<string, string>,
    portToGroup: Record<string, string>,
  ) {
    setState({ portGroups, portRefs, portToGroup });
  }

  function setPortsSelected(ports: string[], selected: boolean) {
    if (ports.length === 0) return;
    // Preserve insertion order (chip strip + timeline lanes follow
    // the user's click sequence + any drag-reorder). applySelection
    // returns the same reference when nothing actually changes, so
    // setState skips the reactive emit on idempotent toggles. Pure
    // logic + tests live in ./selection.
    const next = applySelection(state.selectedPorts, ports, selected);
    if (next === state.selectedPorts) return;
    setState("selectedPorts", next as string[]);
    ensureColors();
    cleanupOrphanedTemps();
  }

  // Replace selectedPorts with an explicit order (used by the chip-row
  // drag-reorder handler). No add/remove logic — caller is responsible
  // for passing a permutation of the current selection.
  function setSelectedPorts(ports: string[]) {
    setState("selectedPorts", ports);
    ensureColors();
    cleanupOrphanedTemps();
  }

  function addTemporaryPort(port: string, afterPort: string, sourceVesselCode: string, sourceVoyageNo: string, orderHint: number, externalRef?: string) {
    if (!port || state.selectedPorts.includes(port)) return;
    if (port in state.temporaries) return;
    // Replace any existing temp wholesale: at-most-one temp lane is
    // visible at a time. Adding a new one drops the previous, since
    // the temp lane is tied to the active selection and the user can
    // only be focused on one stop at once. The single-record write is
    // atomic — the historical "must click twice" bug came from the
    // lifecycle effect firing between writes to parallel maps.
    batch(() => {
      setState("temporaries", reconcile({
        [port]: { anchor: afterPort, sourceVessel: sourceVesselCode, sourceVoyageNo, orderHint },
      }));
      // Pilot stations and anchorages aren't in /api/ports, so
      // state.portRefs has no entry for them — buildLaneLabel would
      // fall back to the full descriptive name ("ZEEBRUGGE - PILOT
      // STATION") instead of the externalRef code ("BEZEEP") shown
      // for regular terminals. Stamp the ref now so the temp lane's
      // label matches the rest of the calendar's terminal-code idiom.
      if (externalRef && !state.portRefs[port]) {
        setState("portRefs", port, externalRef);
      }
    });
    ensureColors();
  }

  function removeTemporaryPort(port: string) {
    if (!(port in state.temporaries)) return;
    setState("temporaries", reconcile(
      Object.fromEntries(Object.entries(state.temporaries).filter(([k]) => k !== port)),
    ));
  }

  function pinTemporaryPort(port: string) {
    const lane = state.temporaries[port];
    if (!lane) return;
    const next = [...state.selectedPorts];
    const idx = lane.anchor ? next.indexOf(lane.anchor) : -1;
    if (idx >= 0) next.splice(idx + 1, 0, port);
    else next.push(port);
    batch(() => {
      setState("temporaries", reconcile(
        Object.fromEntries(Object.entries(state.temporaries).filter(([k]) => k !== port)),
      ));
      setState("selectedPorts", next);
    });
    ensureColors();
  }

  function cleanupOrphanedTemps() {
    const ports = Object.keys(state.temporaries);
    if (ports.length === 0) return;
    const { temps } = pruneOrphanedTemps(state.selectedPorts, ports, laneAnchors(state.temporaries));
    if (temps.length !== ports.length) {
      const keep = new Set(temps);
      setState("temporaries", reconcile(
        Object.fromEntries(Object.entries(state.temporaries).filter(([k]) => keep.has(k))),
      ));
    }
  }

  function toggleGroupExpanded(groupName: string) {
    setState("expandedGroups", groupName, !state.expandedGroups[groupName]);
  }

  function expandGroup(groupName: string, open: boolean) {
    setState("expandedGroups", groupName, open);
  }

  function setPortSearch(s: string) {
    setState("portSearch", s);
  }

  function setPortColor(portName: string, hex: string) {
    // User-set entries land in `portColors` (persisted). Any auto pick
    // for this port becomes shadowed by the user value via the merged
    // `effectivePortColors()` accessor; clear the now-redundant auto
    // entry so it doesn't linger in memory.
    setState("portColors", portName, hex);
    savePortColors(state.portColors);
    if (state.autoPortColors[portName]) {
      const next: Record<string, string> = {};
      for (const [k, v] of Object.entries(state.autoPortColors)) {
        if (k !== portName) next[k] = v;
      }
      setState("autoPortColors", reconcile(next));
    }
  }

  function clearPortColor(portName: string) {
    // Drop the key entirely (vs. setting it to undefined which would
    // persist as `null` in localStorage). Build the next object minus
    // this key, then reconcile in.
    const next: Record<string, string> = {};
    for (const [k, v] of Object.entries(state.portColors)) {
      if (k !== portName) next[k] = v;
    }
    setState("portColors", reconcile(next));
    savePortColors(next);
    // If the port is still in selection, the calendar still wants a
    // swatch — let ensureColors re-seed an auto pick. Otherwise nothing
    // to do; the port has no presence in any colour map.
    ensureColors();
  }

  function ensureColors() {
    const updates: Record<string, string> = {};
    // Temporary lanes get colours from the same pool so their swatch +
    // connection lines + voyage bars share the look of permanent lanes.
    // Auto picks go to `autoPortColors` so the itinerary (which reads
    // `state.portColors` directly) only ever sees user-explicit entries.
    for (const p of [...state.selectedPorts, ...Object.keys(state.temporaries)]) {
      if (state.portColors[p] || state.autoPortColors[p]) continue;
      updates[p] = nextColor({
        ...state.portColors,
        ...state.autoPortColors,
        ...updates,
      });
    }
    if (Object.keys(updates).length > 0) {
      setState("autoPortColors", (prev) => ({ ...prev, ...updates }));
    }
  }

  function resetPortColors() {
    // Wipe user-set entries (persisted) AND runtime auto picks, then
    // re-seed auto for the currently-selected ports so chips don't
    // flash to FALLBACK_HEX grey before the next paint.
    setState("portColors", reconcile({}));
    savePortColors({});
    setState("autoPortColors", reconcile({}));
    ensureColors();
  }

  function setYearMonth(year: number, month: number) {
    setState({ year, month });
  }

  function setQueryPorts(ports: string[]) {
    setState("queryPorts", ports);
  }

  function setViewport(start: Date, end: Date) {
    // No-op guard: fresh Date objects with the same instants would
    // otherwise emit on every cross-tab URL echo (vessels writes v.*,
    // calendar's url-sync re-applies c.start/c.end with new Dates).
    const cur = state.viewport;
    if (cur.start.getTime() === start.getTime() && cur.end.getTime() === end.getTime()) return;
    setState("viewport", { start, end });
  }

  function markMonthLoaded(year: number, month: number) {
    setState("loadedMonths", ymKey(year, month), true);
  }

  function upsertVoyages(voyages: ScheduleVoyage[]) {
    if (voyages.length === 0) return;
    setState("voyages", mergeUpsertVoyages(state.voyages, voyages));
  }

  function setDateEvents(events: DateEvent[]) {
    // reconcile() so the array reference flips even when shallow content
    // matches — Solid stores merge arrays by index when given a fresh
    // array, which can leave deleted tail items in place. reconcile
    // recomputes the diff cleanly.
    setState("dateEvents", reconcile(events));
  }

  function setStatus(text: string, tone: CalendarState["status"]["tone"]) {
    setState("status", { text, tone });
  }

  function setTimelineLoading(active: boolean) {
    setState("isLoadingTimeline", active);
  }

  function setPortsLoading(active: boolean) {
    setState("isLoadingPorts", active);
  }

  function setVesselFilter(code: string | null) {
    // Normalise empty / whitespace strings to null so callers don't
    // need to special-case them. setState skips emitting when the
    // value is already what we'd write.
    const next = code && code.trim() !== "" ? code.trim() : null;
    if (state.vesselFilter === next) return;
    setState("vesselFilter", next);
  }

  function toggleVesselFilter(code: string) {
    const trimmed = (code ?? "").trim();
    if (trimmed === "") return;
    setVesselFilter(state.vesselFilter === trimmed ? null : trimmed);
  }

  function setOprBucket(id: OprBucketId) {
    if (state.oprBucket === id) return;
    // Bucket switch invalidates every cached month — loadedMonths is
    // keyed per (year, month) regardless of bucket, and voyages keeps
    // any code from the previous bucket. Wipe both so the next
    // viewport tick refetches under the new filter.
    batch(() => {
      setState("oprBucket", id);
      setState({ loadedMonths: {}, voyages: {} });
    });
  }

  const value: CalendarContextValue = {
    state,
    setPortCatalogue,
    setPortsSelected,
    setSelectedPorts,
    addTemporaryPort,
    removeTemporaryPort,
    pinTemporaryPort,
    toggleGroupExpanded,
    expandGroup,
    setPortSearch,
    setPortColor,
    clearPortColor,
    ensureColors,
    resetPortColors,
    setYearMonth,
    setQueryPorts,
    setViewport,
    markMonthLoaded,
    upsertVoyages,
    setDateEvents,
    setStatus,
    setTimelineLoading,
    setPortsLoading,
    setVesselFilter,
    toggleVesselFilter,
    setOprBucket,
    effectivePortColors: mergedPortColors,
  };

  // Single source of truth for fetching + landing the port catalogue.
  // Called from the provider's onMount AND from the upstream-auth
  // healthy-transition retry effect below — when getPorts fails during
  // a reauth-needed window, the user shouldn't have to refresh after
  // they complete the auth flow.
  async function loadPortCatalogue(): Promise<void> {
    setPortsLoading(true);
    setStatus("Loading ports…", "loading");
    try {
      const data = await getPorts(10000);
      const groupsObj = data.portGroups ?? {};
      const portGroups: PortGroup[] = Object.keys(groupsObj)
        .map((name) => ({
          name,
          ports: [...(groupsObj[name] ?? [])].sort((a, b) => a.localeCompare(b)),
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
      const portToGroup: Record<string, string> = {};
      for (const g of portGroups) for (const p of g.ports) portToGroup[p] = g.name;
      const portRefs: Record<string, string> = {};
      for (const entry of data.portList ?? []) {
        if (entry.name && entry.externalRef) portRefs[entry.name] = entry.externalRef;
      }
      setPortCatalogue(portGroups, portRefs, portToGroup);
      setStatus(`${portGroups.length} port groups available.`, "info");

      // Trigger a holiday sync as part of the initial date-events fetch.
      // Narrowed to the countries of the user's currently-selected ports
      // (loaded from /api/current-view) so a fresh install doesn't fan
      // out to all ~150 countries in the global catalogue.
      const portCountries = buildPortCountries(portRefs);
      const interesting = new Set<string>();
      for (const p of state.selectedPorts) {
        const c = portCountries.get(p);
        if (c) interesting.add(c);
      }
      const nowYear = new Date().getFullYear();
      const sync: { country: string; year: number }[] = [];
      for (const c of interesting) {
        sync.push({ country: c, year: nowYear });
        sync.push({ country: c, year: nowYear + 1 });
      }
      void getDateEvents(sync)
        .then((events) => setDateEvents(events))
        .catch(() => { /* non-critical; absence just means no event bands */ });
    } catch (err) {
      setStatus(`Failed to load ports: ${(err as Error).message}`, "error");
    } finally {
      setPortsLoading(false);
    }
  }

  // Eager fetches at provider mount (= app start). Both the catalogue and
  // user date events are needed by Settings even when the user lands
  // directly on #/settings without ever visiting the calendar — keeping
  // them gated on Calendar.tsx's onMount left the Settings filters empty.
  onMount(() => { void loadPortCatalogue(); });

  // One-shot migration: split legacy `portColors` entries. Before the
  // user-set/auto split, `ensureColors` persisted every auto pick into
  // `portColors`, so the cache today is a mix of "user picked this in
  // Settings" and "calendar auto-assigned a swatch from the pool".
  // For terminals in the /api/ports catalogue the user can still
  // manage their entry through Settings (clear the × on the row). For
  // ports NOT in the catalogue — pilot stations, anchorages, temp
  // lanes the user revealed in a previous session — there's no UI
  // path to clear them, so the itinerary kept showing the old auto
  // colour even after the refactor. Once the catalogue lands we move
  // those orphan entries into `autoPortColors` (runtime-only) so the
  // itinerary shows gray and `state.portColors` is cleanly user-set.
  let colourSplitMigrationDone = false;
  createEffect(() => {
    if (colourSplitMigrationDone) return;
    if (Object.keys(state.portRefs).length === 0) return;
    colourSplitMigrationDone = true;
    const inCatalogue: Record<string, string> = {};
    const orphaned: Record<string, string> = {};
    for (const [name, hex] of Object.entries(state.portColors)) {
      if (state.portRefs[name]) inCatalogue[name] = hex;
      else orphaned[name] = hex;
    }
    if (Object.keys(orphaned).length === 0) return;
    setState("portColors", reconcile(inCatalogue));
    savePortColors(inCatalogue);
    setState("autoPortColors", (prev) => ({ ...prev, ...orphaned }));
  });

  // Retry the catalogue when upstream auth transitions back to healthy
  // and `portRefs` is still empty — i.e., the initial getPorts failed
  // because the upstream MSAL token had expired. Without this retry the
  // user had to manually refresh after re-authenticating before their
  // saved terminal selection would render. Gated on portRefs being
  // empty so a normal "token rotated cleanly" healthy → still-healthy
  // doesn't refetch the catalogue. `defer: true` skips the initial
  // mount-time call so we don't double-fire alongside the onMount.
  const upstream = useUpstream();
  createEffect(on(
    () => upstream.state().kind,
    (kind) => {
      if (kind !== "healthy") return;
      if (Object.keys(state.portRefs).length > 0) return;
      void loadPortCatalogue();
    },
    { defer: true },
  ));

  return (
    <CalendarContext.Provider value={value}>
      {props.children}
    </CalendarContext.Provider>
  );
};

export function useCalendar(): CalendarContextValue {
  const ctx = useContext(CalendarContext);
  if (!ctx) throw new Error("useCalendar must be used inside CalendarProvider");
  return ctx;
}

export function isMonthLoaded(state: CalendarState, year: number, month: number): boolean {
  return state.loadedMonths[ymKey(year, month)] === true;
}

export function parentTriState(
  selectedPorts: string[],
  group: PortGroup,
): "none" | "some" | "all" {
  const set = new Set(selectedPorts);
  let n = 0;
  for (const p of group.ports) if (set.has(p)) n++;
  if (n === 0) return "none";
  if (n === group.ports.length) return "all";
  return "some";
}
